import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Mail, Send, User, Building2, Phone, Eye, Pencil, UserCircle2 } from "lucide-react";
import { useSendLeadEmail, type LeadEmailType } from "@/hooks/useSendLeadEmail";
import { generateLeadEmail, generateCustomEmail, type PostCallSuccessOptions, type CustomEmailOptions } from "@/lib/leadEmailTemplates";
import { LEAD_SENDERS, resolveSenderFromEmail, type LeadSenderKey } from "@/lib/leadSenders";
import { useAuth } from "@/contexts/AuthContext";
import { Switch } from "@/components/ui/switch";

interface Lead {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  establishment_name: string | null;
  city: string | null;
  email_count: number;
  last_email_type: string | null;
  last_email_sent_at: string | null;
  current_booking_channels?: string[] | null;
  current_management_system?: string[] | null;
}

interface SendLeadEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead | null;
  emailType: LeadEmailType;
  onSuccess?: () => void;
}

const DEFAULT_ZOOM_LINK = "https://us02web.zoom.us/j/2307829642";

export function SendLeadEmailDialog({
  open,
  onOpenChange,
  lead,
  emailType,
  onSuccess,
}: SendLeadEmailDialogProps) {
  const { sendEmail, sending, getEmailTypeLabel } = useSendLeadEmail();
  const { user } = useAuth();

  // Expéditeur sélectionné (auto-détecté depuis l'email du user authentifié)
  const [senderKey, setSenderKey] = useState<LeadSenderKey>(() => resolveSenderFromEmail(user?.email).key);
  useEffect(() => {
    if (open && user?.email) {
      setSenderKey(resolveSenderFromEmail(user.email).key);
    }
  }, [open, user?.email]);
  const sender = LEAD_SENDERS[senderKey];

  // États pour le mode édition (templates prédéfinis)
  const [isEditing, setIsEditing] = useState(false);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
 
   // États pour post_call_success
   const [zoomLink, setZoomLink] = useState(DEFAULT_ZOOM_LINK);
   const [meetingDateTime, setMeetingDateTime] = useState("");
  
  // États pour custom email
  const [customSubject, setCustomSubject] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  // Générer l'aperçu de l'email avec le footer de désinscription
  const emailPreview = useMemo(() => {
    if (!lead) return { subject: "", html: "" };
    
    if (emailType === "custom") {
      if (!customSubject && !customBody) {
        return { subject: "", html: "" };
      }
      const customOptions: CustomEmailOptions = {
        subject: customSubject || "[Objet de l'email]",
        body: customBody || "[Contenu de l'email]",
      };
      const result = generateCustomEmail(
        {
          full_name: lead.full_name,
          establishment_name: lead.establishment_name,
          city: lead.city,
        },
        customOptions,
        sender,
      );
      const unsubscribeFooter = `
        <p style="margin-top: 32px; font-size: 11px; color: #9ca3af;">
          <a href="#" style="color: #9ca3af; text-decoration: none;">Se désinscrire</a>
        </p>
      `;
      const htmlWithFooter = result.html.replace('</body>', `${unsubscribeFooter}</body>`);
      return { subject: result.subject, html: htmlWithFooter };
    }
    
    const successOptions: PostCallSuccessOptions | undefined = 
      emailType === "post_call_success" 
        ? { zoomLink: zoomLink || "[Lien Zoom]", meetingDateTime: meetingDateTime || "[Date et heure]" }
        : undefined;
    
    const baseEmail = generateLeadEmail(
      {
        full_name: lead.full_name,
        establishment_name: lead.establishment_name,
        city: lead.city,
        current_booking_channels: lead.current_booking_channels,
        current_management_system: lead.current_management_system,
      },
      emailType,
      successOptions,
      sender,
    );
    
    const unsubscribeFooter = `
      <p style="margin-top: 32px; font-size: 11px; color: #9ca3af;">
        <a href="#" style="color: #9ca3af; text-decoration: none;">Se désinscrire</a>
      </p>
    `;
    
    const htmlWithFooter = baseEmail.html.replace(
      '</body>',
      `${unsubscribeFooter}</body>`
    );
    
    return { subject: baseEmail.subject, html: htmlWithFooter };
  }, [lead, emailType, zoomLink, meetingDateTime, customSubject, customBody, sender]);

  // Reset l'état d'édition quand le dialogue se ferme
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setIsEditing(false);
      setEditedSubject("");
      setEditedBody("");
      setZoomLink(DEFAULT_ZOOM_LINK);
      setMeetingDateTime("");
      setCustomSubject("");
      setCustomBody("");
      setShowPreview(false);
    }
    onOpenChange(newOpen);
  };

  if (!lead) return null;

  const handleStartEditing = () => {
    setEditedSubject(emailPreview.subject);
    setEditedBody(emailPreview.html);
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedSubject("");
    setEditedBody("");
  };

   // Vérifier si les champs requis sont remplis pour post_call_success ou custom
   const canSend = emailType === "custom" 
     ? (customSubject.trim() !== "" && customBody.trim() !== "")
     : emailType !== "post_call_success" || (zoomLink.trim() !== "" && meetingDateTime.trim() !== "");
 
  const handleSend = async () => {
    const result = await sendEmail({
      leadId: lead.id,
      emailType,
      senderKey,
      ...(emailType === "custom" && {
        customSubject: customSubject,
        customBody: customBody,
      }),
      ...(emailType !== "custom" && isEditing && {
        customSubject: editedSubject,
        customBody: editedBody,
      }),
      ...(emailType === "post_call_success" && {
        zoomLink,
        meetingDateTime,
      }),
    });

    if (result.success) {
      handleOpenChange(false);
      onSuccess?.();
    }
  };

  const emailTypeInfo = {
    first_contact: {
      title: "Premier contact",
      description: "Email de présentation personnalisé basé sur les informations du prospect",
      icon: "✉️",
      color: "bg-blue-100 text-blue-800",
    },
    followup: {
      title: "Relance",
      description: "Email de suivi avec témoignage client et rappel de la proposition",
      icon: "🔄",
      color: "bg-amber-100 text-amber-800",
    },
    post_call: {
      title: "Post-appel",
      description: "Email de suivi après une tentative d'appel sans réponse",
      icon: "📞",
      color: "bg-purple-100 text-purple-800",
    },
    post_call_failed: {
      title: "Post-appel (échec)",
      description: "Email de suivi après un appel qui n'a pas abouti à une conversion",
      icon: "📞❌",
      color: "bg-red-100 text-red-800",
    },
    post_call_success: {
      title: "Post-appel (succès)",
      description: "Confirmation de rendez-vous Zoom après un appel réussi",
      icon: "📞✅",
      color: "bg-green-100 text-green-800",
    },
    custom: {
      title: "Email libre",
      description: "Rédigez un email personnalisé avec la structure Piktable",
      icon: "✏️",
      color: "bg-slate-100 text-slate-800",
    },
  };

  const info = emailTypeInfo[emailType];

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-pink-500" />
            Envoyer un email
          </DialogTitle>
          <DialogDescription>
            Confirmer l'envoi d'un email à ce prospect
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          {/* Lead info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{lead.full_name}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Mail className="w-3 h-3" />
              {lead.email}
            </div>
            {lead.establishment_name && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="w-3 h-3" />
                {lead.establishment_name}
                {lead.city && ` • ${lead.city}`}
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="w-3 h-3" />
                {lead.phone}
              </div>
            )}
          </div>

          {/* Email type */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">{info.icon}</span>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium">{info.title}</h4>
                  <Badge variant="outline" className={info.color}>
                    {getEmailTypeLabel(emailType)}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {info.description}
                </p>
              </div>
            </div>
          </div>
           
           {/* Champs pour post_call_success */}
           {emailType === "post_call_success" && (
             <div className="border rounded-lg p-4 space-y-4 bg-green-50/50">
               <div className="space-y-2">
                 <Label htmlFor="meetingDateTime" className="flex items-center gap-2">
                   📅 Date et heure du rendez-vous <span className="text-destructive">*</span>
                 </Label>
                 <Input
                   id="meetingDateTime"
                   placeholder="Ex: Jeudi 6 février à 14h00"
                   value={meetingDateTime}
                   onChange={(e) => setMeetingDateTime(e.target.value)}
                 />
               </div>
               <div className="space-y-2">
                 <Label htmlFor="zoomLink" className="flex items-center gap-2">
                   📍 Lien Zoom <span className="text-destructive">*</span>
                 </Label>
                 <Input
                   id="zoomLink"
                   placeholder="Ex: https://zoom.us/j/123456789"
                   value={zoomLink}
                   onChange={(e) => setZoomLink(e.target.value)}
                 />
               </div>
             </div>
           )}
           
           {/* Formulaire pour email libre (custom) */}
           {emailType === "custom" ? (
             <div className="space-y-4">
               <div className="space-y-2">
                 <Label htmlFor="customSubject">
                   Objet <span className="text-destructive">*</span>
                 </Label>
                 <Input
                   id="customSubject"
                   placeholder="Ex: Suite à notre échange - Restaurant XYZ"
                   value={customSubject}
                   onChange={(e) => setCustomSubject(e.target.value)}
                 />
               </div>
               <div className="space-y-2">
                 <Label htmlFor="customBody">
                   Contenu (texte simple, la signature sera ajoutée automatiquement) <span className="text-destructive">*</span>
                 </Label>
                 <Textarea
                   id="customBody"
                   placeholder={`Bonjour,\n\nMerci encore pour notre échange...\n\nÀ disposition si besoin.\nBonne suite et à très vite,\n\nMaxime`}
                   value={customBody}
                   onChange={(e) => setCustomBody(e.target.value)}
                   className="min-h-[200px]"
                 />
                 <p className="text-xs text-muted-foreground">
                   Les URLs seront automatiquement transformées en liens cliquables.
                 </p>
               </div>
               
               {/* Toggle aperçu */}
               <div className="flex items-center justify-between">
                 <Label htmlFor="showPreview" className="flex items-center gap-2 cursor-pointer">
                   <Eye className="w-4 h-4" />
                   Afficher l'aperçu du rendu
                 </Label>
                 <Switch
                   id="showPreview"
                   checked={showPreview}
                   onCheckedChange={setShowPreview}
                 />
               </div>
               
               {/* Aperçu du rendu */}
               {showPreview && customBody && (
                 <div className="border rounded-lg overflow-hidden">
                   <div className="bg-muted px-3 py-2 border-b flex items-center gap-2">
                     <Eye className="w-4 h-4 text-muted-foreground" />
                     <span className="text-sm font-medium">Aperçu du rendu</span>
                   </div>
                   <ScrollArea className="h-[280px]">
                     <div 
                       className="p-4 bg-white"
                       dangerouslySetInnerHTML={{ __html: emailPreview.html }}
                     />
                   </ScrollArea>
                 </div>
               )}
             </div>
           ) : (
             <>
               {/* Email preview ou éditeur pour templates prédéfinis */}
               <div className="border rounded-lg overflow-hidden">
                 <div className="bg-muted px-3 py-2 border-b flex items-center gap-2">
                   {isEditing ? (
                     <>
                       <Pencil className="w-4 h-4 text-muted-foreground" />
                       <span className="text-sm font-medium">Modifier l'email</span>
                     </>
                   ) : (
                     <>
                       <Eye className="w-4 h-4 text-muted-foreground" />
                       <span className="text-sm font-medium">Aperçu de l'email</span>
                     </>
                   )}
                 </div>
                 
                 {/* Subject */}
                 <div className="bg-muted/30 px-4 py-2 border-b">
                   {isEditing ? (
                     <div className="space-y-1">
                       <Label htmlFor="subject" className="text-xs">Objet</Label>
                       <Input
                         id="subject"
                         value={editedSubject}
                         onChange={(e) => setEditedSubject(e.target.value)}
                         className="h-8"
                       />
                     </div>
                   ) : (
                     <div className="flex items-center gap-2">
                       <Badge variant="secondary" className="text-xs">Objet</Badge>
                       <span className="text-sm font-medium">{emailPreview.subject}</span>
                     </div>
                   )}
                 </div>
                 
                 {/* Body */}
                 {isEditing ? (
                   <Textarea
                     value={editedBody}
                     onChange={(e) => setEditedBody(e.target.value)}
                     className="min-h-[280px] rounded-none border-0 resize-none font-mono text-xs"
                     placeholder="Contenu HTML de l'email..."
                   />
                 ) : (
                   <ScrollArea className="h-[280px]">
                     <div 
                       className="p-4 bg-white"
                       dangerouslySetInnerHTML={{ __html: emailPreview.html }}
                     />
                   </ScrollArea>
                 )}
               </div>
             </>
           )}

          {/* Previous emails info */}
          {lead.email_count > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="text-amber-800">
                ⚠️ Ce prospect a déjà reçu {lead.email_count} email{lead.email_count > 1 ? "s" : ""}.
                {lead.last_email_type && (
                  <span className="block text-amber-600 mt-1">
                    Dernier envoi : {lead.last_email_type === "first_contact" ? "Premier contact" : "Relance"}
                    {lead.last_email_sent_at && (
                      <> le {new Date(lead.last_email_sent_at).toLocaleDateString("fr-CH")}</>
                    )}
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Sender selector */}
          <div className="border rounded-lg p-4 bg-muted/30 space-y-2">
            <Label htmlFor="sender" className="flex items-center gap-2 text-sm">
              <UserCircle2 className="w-4 h-4" />
              Envoyé par
            </Label>
            <Select value={senderKey} onValueChange={(v) => setSenderKey(v as LeadSenderKey)}>
              <SelectTrigger id="sender">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(LEAD_SENDERS).map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.fullName} — {s.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              L'email sera envoyé depuis <strong>{sender.email}</strong> avec la signature de {sender.fullName}.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button 
            variant="outline" 
            onClick={() => handleOpenChange(false)} 
            disabled={sending}
          >
            Annuler
          </Button>
          
          {emailType === "custom" ? (
            // Pour email libre, juste le bouton envoyer
            <Button onClick={handleSend} disabled={sending || !canSend} className="gap-2">
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Envoyer
                </>
              )}
            </Button>
          ) : !isEditing ? (
            <>
              <Button 
                variant="secondary" 
                onClick={handleStartEditing} 
                disabled={sending}
                className="gap-2"
              >
                <Pencil className="w-4 h-4" />
                Modifier
              </Button>
               <Button onClick={handleSend} disabled={sending || !canSend} className="gap-2">
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Envoyer
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={handleCancelEditing}
              >
                Annuler les modifications
              </Button>
               <Button onClick={handleSend} disabled={sending || !canSend} className="gap-2">
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Envoyer l'email modifié
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
